import { useEffect, useState } from "react";
import { Download, Share2, X } from "lucide-react";

const DISMISSED_KEY = "mpy_install_dismissed_at";
const DISMISS_DAYS = 7;

/**
 * Floating "Install App" prompt.
 *
 * Behaviour:
 *  - On Android/Chrome/Edge: intercepts the native `beforeinstallprompt` event and
 *    shows a native-looking button. Clicking it fires the real system install dialog.
 *  - On iOS Safari (no beforeinstallprompt): shows a custom guide with the 3-tap
 *    procedure ("Partager → Sur l'écran d'accueil") when the app isn't already
 *    running in standalone mode.
 *  - The prompt is dismissable and won't reappear for 7 days.
 *  - Hidden entirely when the app is already installed (display-mode: standalone).
 */
export function InstallAppBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);

  // Detect if we're already running as an installed PWA
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true);

  // Detect iOS Safari (only place we need custom flow)
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = isIos && !/CriOS|FxiOS|EdgiOS/.test(ua);

  useEffect(() => {
    // Was the banner recently dismissed?
    try {
      const raw = localStorage.getItem(DISMISSED_KEY);
      if (raw) {
        const ms = Number(raw);
        if (Date.now() - ms < DISMISS_DAYS * 24 * 3600 * 1000) {
          setDismissed(true);
        }
      }
    } catch (_) { /* ignore */ }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const onInstalled = () => {
      setDeferredPrompt(null);
      // Consider it dismissed forever
      try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch (_) {}
      setDismissed(true);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch (_) {}
    setDismissed(true);
  };

  const install = async () => {
    if (isIos && isSafari) {
      setShowIosSheet(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch (_) { /* ignore */ }
    setDeferredPrompt(null);
  };

  // Nothing to show?
  if (isStandalone || dismissed) return null;
  const canShow = deferredPrompt || (isIos && isSafari);
  if (!canShow) return null;

  return (
    <>
      {/* Compact bottom banner */}
      <div
        role="dialog"
        aria-labelledby="mpy-install-title"
        data-testid="install-banner"
        className="fixed left-1/2 -translate-x-1/2 z-40 max-w-md w-[calc(100%-1.5rem)] bg-white border-2 border-[color:var(--coral)]/40 shadow-2xl rounded-2xl p-3.5 pr-2 flex items-center gap-3"
        style={{ bottom: "calc(72px + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 shadow-sm">
          <img src="/branding/pwa/icon-192.png" alt="MPY" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div id="mpy-install-title" className="brand-wordmark text-[color:var(--deep)] text-base leading-tight">
            Installer l'app
          </div>
          <div className="text-[11px] text-slate-500 leading-tight">
            Accès rapide depuis ton écran d'accueil
          </div>
        </div>
        <button
          onClick={install}
          data-testid="install-btn"
          className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-[color:var(--coral)] text-white text-xs font-black uppercase tracking-wider hover:scale-105 transition"
        >
          <Download className="h-3.5 w-3.5" />
          Installer
        </button>
        <button
          onClick={dismiss}
          data-testid="install-dismiss"
          className="w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition flex-shrink-0"
          aria-label="Ne plus afficher"
          title="Ne plus afficher pendant 7 jours"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* iOS Safari — instruction sheet */}
      {showIosSheet && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center px-4"
          onClick={() => setShowIosSheet(false)}
          data-testid="install-ios-sheet"
        >
          <div
            className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full p-6 pb-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-[color:var(--coral)] mb-1">
                  Installer sur iPhone
                </div>
                <h3 className="brand-wordmark text-2xl text-[color:var(--deep)]">
                  Ajouter à l'écran d'accueil
                </h3>
              </div>
              <button
                onClick={() => setShowIosSheet(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <ol className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[color:var(--sunshine)] text-[color:var(--deep)] font-black text-xs flex items-center justify-center">
                  1
                </span>
                <span className="flex-1 leading-relaxed">
                  Appuie sur l'icône{" "}
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 font-bold">
                    <Share2 className="h-3.5 w-3.5" /> Partager
                  </span>{" "}
                  en bas de ton écran Safari
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[color:var(--sunshine)] text-[color:var(--deep)] font-black text-xs flex items-center justify-center">
                  2
                </span>
                <span className="flex-1 leading-relaxed">
                  Fais défiler et appuie sur{" "}
                  <b>« Sur l'écran d'accueil »</b>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[color:var(--sunshine)] text-[color:var(--deep)] font-black text-xs flex items-center justify-center">
                  3
                </span>
                <span className="flex-1 leading-relaxed">
                  Valide en tapant <b>« Ajouter »</b> en haut à droite
                </span>
              </li>
            </ol>

            <div className="mt-5 text-[11px] text-slate-500 bg-slate-50 rounded-xl p-3 border border-slate-100">
              💡 Une fois installée, l'app Ma Petite Yole s'ouvrira en plein écran comme une vraie app, sans la barre Safari.
            </div>

            <button
              onClick={() => setShowIosSheet(false)}
              className="w-full mt-5 h-11 rounded-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white font-bold text-sm"
            >
              J'ai compris
            </button>
          </div>
        </div>
      )}
    </>
  );
}
